/**
 * E002-T035: HistoryPanel Component (Run-Level Logging)
 * E002-T046: Backend Logs - Single Source of Truth
 *
 * Displays execution history for an automation block:
 * - List of recent executions (per Run, not per Row)
 * - Input/Process/Output stages
 * - Statistics summary
 *
 * SINGLE SOURCE OF TRUTH:
 * - All logs are stored in PostgreSQL (backend)
 * - Active log (running) comes from LoggingService (in-memory, until persisted)
 * - No more BlockModel storage
 */

import { Button, Loading, useConfirmModal } from '@affine/component';
import { AnimatedRegion } from '@affine/core/components/animation/animated-region';
import { DisclosureChevron } from '@affine/core/components/animation/disclosure-chevron';
import { workspaceApiFetch } from '@affine/core/modules/cloud';
import { i18nTime, useI18n } from '@affine/i18n';
import { streamLainAnimationController } from '@blocksuite/affine-shared/utils';
import { DeleteIcon } from '@blocksuite/icons/rc';
import type { AutomationTerminalReason } from '@streamlain/automation-safety-contract';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { WorkspaceService } from '../../../workspace';
import { AutomationService } from '../../services/automation';
import {
  type ExecutionLog,
  hasActiveLongRunningOps,
  LoggingService,
  type LongRunningOpInfo,
  type WorkflowNodeStep,
} from '../../services/logging';
import { workflowWorkspaceRoutes } from '../../services/workflow-workspace-routes';
import { formatWorkflowErrorMessage } from '../../utils/workflow-error-message';
import { formatSttBatchError } from '../../workflow-viewer/stt-batch-error';
import * as panelStyles from '../automation-panel.css';
import * as styles from './history-panel.css';
import { LongRunningOpRow } from './history-row-variants/long-running-op-row';
import { DataRow, ErrorBox } from './history-row-variants/row-primitives';
import { WrittenTablesRows } from './history-row-variants/written-tables';
import {
  historyForegroundPolicy,
  useHistoryRunActions,
} from './history-run-actions';
import {
  formatHistoryDurationMs,
  formatHistoryErrorMessage,
  formatHistoryExecutionStatusLabel,
  formatHistoryOutputModeLabel,
  formatHistoryProcessStatusLabel,
  formatHistoryTriggerLabel,
  historyRunTimes,
  historyRunWritesNothing,
  readHistoryWaitingLimit,
} from './history-status-labels';

interface HistoryPanelProps {
  /** Block ID to show history for */
  blockId: string;
}

// =============================================================================
// Utility Functions
// =============================================================================

/** Relative within a day, then date and time; always in the UI language. */
/** Why the safety net ended a run or paused the automation. */
const TERMINAL_REASON_TEXT = {
  cycle_blocked: 'com.streamlain.streamlain.history.cycle-blocked',
  suspected_external_loop:
    'com.streamlain.streamlain.history.external-loop-suspected',
  mail_reply_loop: 'com.streamlain.streamlain.history.mail-reply-loop',
  import_flood: 'com.streamlain.streamlain.history.import-flood',
} as const satisfies Record<AutomationTerminalReason, string>;

const formatTimestamp = (timestamp: number): string =>
  i18nTime(timestamp, {
    relative: { max: [1, 'day'], accuracy: 'minute' },
    absolute: {
      accuracy: 'minute',
      noYear: new Date(timestamp).getFullYear() === new Date().getFullYear(),
    },
  });

const formatMediaProgressParts = (
  progress: NonNullable<ExecutionLog['process']>['mediaProgress']
): string[] => {
  if (!progress) return [];

  return [
    progress.message ?? progress.activity ?? null,
    progress.percent !== undefined ? `${progress.percent}%` : null,
    progress.bytesTotal !== undefined
      ? `${formatBytes(progress.bytesDone ?? 0)} / ${formatBytes(
          progress.bytesTotal
        )}`
      : null,
    progress.bitrate ?? null,
    progress.fps !== undefined ? `${progress.fps.toFixed(1)} fps` : null,
    progress.timeRemainingMs !== undefined
      ? `ETA ${Math.max(0, Math.ceil(progress.timeRemainingMs / 1000))}s`
      : null,
  ].filter((part): part is string => Boolean(part));
};

const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }
  return `${value >= 10 || unitIndex === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
};

const normalizeStepName = (value: string | undefined): string =>
  value?.trim().toLowerCase().replace(/\s+/g, ' ') ?? '';

const isProgressForStep = (
  step: WorkflowNodeStep,
  progress: NonNullable<ExecutionLog['process']>['mediaProgress']
): boolean => {
  if (!progress) return false;
  if (step.nodeId && progress.stepId && step.nodeId === progress.stepId) {
    return true;
  }

  const stepName = normalizeStepName(step.name);
  const progressName = normalizeStepName(progress.stepName);
  return Boolean(stepName && progressName && stepName === progressName);
};

const hasStepForProgress = (
  steps: WorkflowNodeStep[] | undefined,
  progress: NonNullable<ExecutionLog['process']>['mediaProgress']
): boolean =>
  Boolean(progress && steps?.some(step => isProgressForStep(step, progress)));

// =============================================================================
// Sub-Components
// =============================================================================

const logStatusTone = (
  status: ExecutionLog['status'],
  rowsWritten: number | undefined,
  queued: boolean
): styles.HistoryTone => {
  switch (status) {
    case 'running':
      return queued ? 'warning' : 'active';
    case 'awaiting_external':
      return 'warning';
    case 'completed':
      return rowsWritten === 0 ? 'warning' : 'success';
    case 'failed':
      return 'error';
    default:
      return 'neutral';
  }
};

const processStatusTone = (
  status: NonNullable<ExecutionLog['process']>['status']
): styles.HistoryTone =>
  status === 'success'
    ? 'success'
    : status === 'running'
      ? 'active'
      : status === 'queued'
        ? 'warning'
        : 'error';

const StatusPill: FC<{ tone: styles.HistoryTone; children: ReactNode }> = ({
  tone,
  children,
}) => (
  <span className={styles.statusPill}>
    <span
      className={clsx(styles.dot, styles.dotTone[tone])}
      aria-hidden="true"
    />
    {children}
  </span>
);

/**
 * sl-trm: completed runs without written rows read as a warning.
 * sl-1ahs.7.5: execution modes are only 'execute' | 'trigger'.
 */
const StatusBadge: FC<{
  status: ExecutionLog['status'];
  rowsWritten?: number;
  queued?: boolean;
  /** The queued run waits for a plan limit. */
  waitingForLimit?: boolean;
}> = ({ status, rowsWritten, queued = false, waitingForLimit = false }) => {
  const t = useI18n();
  return (
    <StatusPill tone={logStatusTone(status, rowsWritten, queued)}>
      {waitingForLimit
        ? t['com.streamlain.streamlain.history.waiting-limit']()
        : queued
          ? t['com.streamlain.streamlain.history.queued']()
          : formatHistoryExecutionStatusLabel(t, status, queued)}
    </StatusPill>
  );
};

const StageSection: FC<{ title: string; children: ReactNode }> = ({
  title,
  children,
}) => (
  <section className={styles.stage}>
    <div className={styles.stageTitle}>{title}</div>
    {children}
  </section>
);

const JsonPreview: FC<{ data: unknown; capped?: boolean }> = ({
  data,
  capped = false,
}) => (
  <pre className={clsx(styles.json, capped && styles.jsonCapped)}>
    {JSON.stringify(data, null, 2)}
  </pre>
);

// =============================================================================
// NodeStepEntry Component - Expandable node step with Input/Output data
// =============================================================================

interface NodeStepEntryProps {
  step: WorkflowNodeStep;
  isExpanded: boolean;
  onToggle: () => void;
}

const NodeStepEntry: FC<NodeStepEntryProps> = ({
  step,
  isExpanded,
  onToggle,
}) => {
  const t = useI18n();
  const isRunning = step.status === 'running';
  const progressParts = formatMediaProgressParts(step.mediaProgress);
  const progressMessage =
    step.mediaProgress?.message ?? step.mediaProgress?.activity;
  const progressDetails = progressParts
    .filter(part => part !== progressMessage)
    .join(' · ');
  const translatedError = formatSttBatchError(
    t,
    step.errorCode,
    step.errorDetails
  );
  const errorMessage =
    translatedError ?? (step.error ? formatHistoryErrorMessage(t) : null);

  return (
    <div className={styles.step} data-expanded={isExpanded}>
      <button
        type="button"
        className={styles.stepHeader}
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <DisclosureChevron expanded={isExpanded} className={styles.chevron} />
        {isRunning ? (
          <Loading size={12} />
        ) : (
          <span
            className={clsx(
              styles.dot,
              styles.dotTone[step.status === 'success' ? 'success' : 'error']
            )}
            aria-hidden="true"
          />
        )}
        <span className={styles.stepName}>{step.name}</span>
        <span className={styles.stepDuration}>
          {isRunning
            ? t['com.streamlain.streamlain.history.running']()
            : `${step.duration}ms`}
        </span>
      </button>

      {step.mediaProgress && (
        <div className={styles.stepProgress}>
          {progressMessage && (
            <span className={styles.ellipsis}>{progressMessage}</span>
          )}
          {progressDetails && (
            <span className={clsx(styles.ellipsis, styles.tabular)}>
              {progressDetails}
            </span>
          )}
        </div>
      )}

      <AnimatedRegion expanded={isExpanded}>
        <div className={styles.stepDetails}>
          {/* sl-j7kp.3.2: Step input data when available, else the summary */}
          {step.inputData && step.inputData.length > 0 ? (
            <div className={styles.stepIo}>
              <span className={styles.stepIoLabel}>
                {t['com.streamlain.streamlain.history.input-label']()}
              </span>
              <JsonPreview data={step.inputData} capped />
            </div>
          ) : step.inputSummary && step.inputSummary.length > 0 ? (
            <div className={styles.stepIo}>
              <span className={styles.stepIoLabel}>
                {t['com.streamlain.streamlain.history.input-label']()}
              </span>{' '}
              {step.inputSummary.join(', ')}
            </div>
          ) : null}

          {/* sl-j7kp.3.2: Step output data when available, else the summary */}
          {step.outputData && step.outputData.length > 0 ? (
            <div className={styles.stepIo}>
              <span className={styles.stepIoLabel}>
                {t['com.streamlain.streamlain.history.output-label']()}
              </span>
              <JsonPreview data={step.outputData} capped />
            </div>
          ) : step.outputSummary && step.outputSummary.length > 0 ? (
            <div className={styles.stepIo}>
              <span className={styles.stepIoLabel}>
                {t['com.streamlain.streamlain.history.output-label']()}
              </span>{' '}
              {step.outputSummary.join(', ')}
            </div>
          ) : null}

          {errorMessage ? (
            <ErrorBox message={errorMessage} size="small" />
          ) : null}
        </div>
      </AnimatedRegion>
    </div>
  );
};

// =============================================================================
// LongRunningOp Sub-Components (sl-67kc.3.3)
// =============================================================================
//
// The `LongRunningOpRow` variant + its helpers live in
// `./history-row-variants/long-running-op-row.tsx`. The split was
// introduced by sl-67kc.3.3 so every long-running-op kind (stt today,
// video-transcode + bulk-ai later) can land its own row variant
// without bloating this panel further.

// =============================================================================
// LogEntry Component
// =============================================================================

/** One run of the history; the row history reuses it for several automations. */
export const LogEntry: FC<{
  log: ExecutionLog;
  isExpanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onCancelOp: (op: LongRunningOpInfo) => Promise<void>;
  onCancelExecution: (executionId: string) => Promise<void>;
  onRetryOp: (op: LongRunningOpInfo) => Promise<void>;
  onDownloadOp: (op: LongRunningOpInfo) => Promise<void>;
  isActive?: boolean;
  rowRef?: (el: HTMLDivElement | null) => void;
}> = ({
  log,
  isExpanded,
  onToggle,
  onDelete,
  onCancelOp,
  onCancelExecution,
  onRetryOp,
  onDownloadOp,
  isActive,
  rowRef,
}) => {
  const t = useI18n();
  const [expandedStepIndex, setExpandedStepIndex] = useState<number | null>(
    null
  );
  // E018-S002: Visual feedback for copy button
  const [copied, setCopied] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  // sl-67kc.3.3: Delete-guard — block delete while any LRO is still
  // active so we never orphan a parked SuspendedExecution row.
  const deleteBlocked = hasActiveLongRunningOps(log);
  const outputModeLabel = log.output
    ? formatHistoryOutputModeLabel(t, log.output.mode)
    : null;
  const canCancelExecution = log.status === 'running';
  const isQueuedRun =
    log.status === 'running' && log.process?.status === 'queued';
  const runTimes = historyRunTimes({
    startedAt: log.startedAt,
    completedAt: log.completedAt,
    queueWaitMs: log.process?.queueWaitMs,
  });
  const waitingLimit = isQueuedRun
    ? readHistoryWaitingLimit(log.process?.response)
    : null;

  const handleCancelExecution = useCallback(
    async (e: React.MouseEvent): Promise<void> => {
      e.stopPropagation();
      if (canceling || !canCancelExecution) return;
      setCanceling(true);
      setCancelError(null);
      try {
        await onCancelExecution(log.id);
      } catch (err) {
        setCancelError(err instanceof Error ? err.message : String(err));
      } finally {
        setCanceling(false);
      }
    },
    [canCancelExecution, canceling, log.id, onCancelExecution]
  );

  const copyRun = (e: React.MouseEvent): void => {
    e.stopPropagation();
    const runData = {
      id: log.id,
      mode: log.mode,
      status: log.status,
      startedAt: log.startedAt,
      completedAt: log.completedAt,
      input: log.input,
      process: log.process,
      output: log.output,
      error: log.error,
    };
    navigator.clipboard
      .writeText(JSON.stringify(runData, null, 2))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  };

  return (
    <div
      ref={rowRef}
      className={styles.entry}
      data-active={isActive === true}
      data-expanded={isExpanded}
    >
      <div className={styles.entryHeader}>
        <button
          type="button"
          className={styles.entryToggle}
          onClick={onToggle}
          aria-expanded={isExpanded}
        >
          <DisclosureChevron expanded={isExpanded} className={styles.chevron} />
          {log.automationName !== undefined && (
            <span className={styles.automationName}>
              {log.automationName ??
                t['com.streamlain.automation.row-history.unnamed']()}
            </span>
          )}
          <span className={styles.triggerLabel}>
            {formatHistoryTriggerLabel(t, log.triggerType)}
          </span>
          <StatusBadge
            status={log.status}
            rowsWritten={log.output?.rowsWritten}
            queued={isQueuedRun}
            waitingForLimit={waitingLimit !== null}
          />
          <time
            className={styles.time}
            dateTime={new Date(log.startedAt).toISOString()}
          >
            {formatTimestamp(log.startedAt)}
          </time>
        </button>
        {canCancelExecution && (
          <button
            type="button"
            className={styles.stopButton}
            onClick={e => {
              void handleCancelExecution(e);
            }}
            disabled={canceling}
            aria-label={t['com.streamlain.streamlain.history.stop-execution']()}
            title={
              canceling
                ? t['com.streamlain.streamlain.history.stopping-execution']()
                : t['com.streamlain.streamlain.history.stop-execution']()
            }
          >
            <span className={styles.stopGlyph} aria-hidden="true" />
          </button>
        )}
      </div>

      <AnimatedRegion expanded={isExpanded}>
        <div className={styles.details}>
          {/* Duration + copy */}
          <div className={styles.detailsMeta}>
            {runTimes?.waitMs != null ? (
              <>
                <span>
                  <span className={styles.muted}>
                    {t['com.streamlain.streamlain.history.wait-time']()}
                  </span>{' '}
                  <span className={styles.tabular}>
                    {formatHistoryDurationMs(runTimes.waitMs)}
                  </span>
                </span>
                <span>
                  <span className={styles.muted}>
                    {t['com.streamlain.streamlain.history.run-time']()}
                  </span>{' '}
                  <span className={styles.tabular}>
                    {formatHistoryDurationMs(runTimes.runMs)}
                  </span>
                </span>
              </>
            ) : (
              <span>
                <span className={styles.muted}>
                  {t['com.streamlain.streamlain.history.duration']()}
                </span>{' '}
                <span className={styles.tabular}>
                  {runTimes
                    ? formatHistoryDurationMs(runTimes.runMs)
                    : t['com.streamlain.streamlain.history.running']()}
                </span>
              </span>
            )}
            <button
              type="button"
              className={styles.smallButton}
              data-copied={copied}
              onClick={copyRun}
            >
              {copied
                ? t['com.streamlain.streamlain.history.copied']()
                : t['com.streamlain.streamlain.history.copy-run']()}
            </button>
          </div>

          {/* sl-67kc.3.3: External long-running ops (STT today). */}
          {log.longRunningOps && log.longRunningOps.length > 0 && (
            <StageSection
              title={t['com.streamlain.streamlain.history.lro.section-title']()}
            >
              {log.longRunningOps.map(op => (
                <LongRunningOpRow
                  key={op.suspendedExecutionId}
                  op={op}
                  onCancel={() => onCancelOp(op)}
                  onRetry={() => onRetryOp(op)}
                  onDownload={() => onDownloadOp(op)}
                />
              ))}
            </StageSection>
          )}

          {/* INPUT Stage */}
          {log.input && (
            <StageSection
              title={t['com.streamlain.streamlain.history.stage-input']()}
            >
              <DataRow
                label={t['com.streamlain.streamlain.history.source-table']()}
                value={log.input.sourceTable}
              />
              <DataRow
                label={t['com.streamlain.streamlain.history.total-rows']()}
                value={log.input.totalRows}
              />
              <DataRow
                label={t['com.streamlain.streamlain.history.passed-filter']()}
                value={log.input.passedFilter}
                tone="success"
              />
              <DataRow
                label={t['com.streamlain.streamlain.history.filtered-out']()}
                value={log.input.filteredOut}
                tone={log.input.filteredOut > 0 ? 'warning' : undefined}
              />
              {log.input.filterConfig && (
                <>
                  <div className={styles.subLabel}>
                    {t['com.streamlain.streamlain.history.filter-config']()}
                  </div>
                  <JsonPreview data={log.input.filterConfig} />
                </>
              )}
              {log.input.selectedColumns &&
                log.input.selectedColumns.length > 0 && (
                  <>
                    <div className={styles.subLabel}>
                      {t[
                        'com.streamlain.streamlain.history.selected-columns'
                      ]()}
                    </div>
                    <JsonPreview data={log.input.selectedColumns} />
                  </>
                )}
              {log.input.payloads.length > 0 && (
                <>
                  <div className={styles.subLabel}>
                    {t['com.streamlain.streamlain.history.payloads']({
                      count: String(log.input.payloads.length),
                    })}
                  </div>
                  <JsonPreview data={log.input.payloads} />
                </>
              )}
            </StageSection>
          )}

          {/* PROCESS Stage */}
          {log.process && (
            <StageSection
              title={t['com.streamlain.streamlain.history.stage-process']()}
            >
              <DataRow
                label={t['com.streamlain.streamlain.history.duration']()}
                value={`${log.process.duration}ms`}
              />
              <DataRow
                label={t['com.streamlain.streamlain.history.status']()}
                value={
                  waitingLimit
                    ? t['com.streamlain.streamlain.history.waiting-limit']()
                    : formatHistoryProcessStatusLabel(t, log.process.status)
                }
                tone={processStatusTone(log.process.status)}
              />
              {waitingLimit && (
                <DataRow
                  label={
                    waitingLimit.limit === 'webhook_concurrency'
                      ? t[
                          'com.streamlain.streamlain.history.waiting-limit.webhook-concurrency'
                        ]()
                      : t[
                          'com.streamlain.streamlain.history.waiting-limit.workspace-concurrency'
                        ]()
                  }
                  value={t[
                    'com.streamlain.streamlain.history.waiting-limit.value'
                  ]({
                    current: String(waitingLimit.current),
                    max: String(waitingLimit.max),
                  })}
                  tone="warning"
                />
              )}
              {log.process.mediaProgress &&
                !hasStepForProgress(
                  log.process.nodeSteps,
                  log.process.mediaProgress
                ) && (
                  <div className={styles.progressRow}>
                    <Loading size={12} />
                    <span className={styles.progressName}>
                      {log.process.mediaProgress.stepName ??
                        t['com.streamlain.streamlain.history.running']()}
                    </span>
                    <span className={styles.progressValue}>
                      {formatMediaProgressParts(log.process.mediaProgress).join(
                        ' · '
                      ) || t['com.streamlain.streamlain.history.running']()}
                    </span>
                  </div>
                )}
              {log.process.runtimeEvents &&
                log.process.runtimeEvents.length > 0 && (
                  <div className={styles.events}>
                    {log.process.runtimeEvents.slice(-5).map(event => (
                      <div
                        key={`${event.timestamp}-${event.stepId ?? ''}-${
                          event.activity ?? event.phase ?? ''
                        }`}
                        className={styles.eventRow}
                      >
                        <span className={styles.ellipsis}>
                          {event.message ??
                            event.activity ??
                            event.stepName ??
                            t['com.streamlain.streamlain.history.running']()}
                        </span>
                        {event.bytesTotal !== undefined ? (
                          <span className={styles.nowrap}>
                            {formatBytes(event.bytesDone ?? 0)} /{' '}
                            {formatBytes(event.bytesTotal)}
                          </span>
                        ) : event.percent !== undefined ? (
                          <span className={styles.nowrap}>
                            {event.percent}%
                          </span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              {log.process.nodeSteps && log.process.nodeSteps.length > 0 && (
                <>
                  <div className={styles.subLabel}>
                    {t['com.streamlain.streamlain.history.steps']({
                      count: String(log.process.nodeSteps.length),
                    })}
                  </div>
                  <div className={styles.steps}>
                    {log.process.nodeSteps.map((step, i) => (
                      <NodeStepEntry
                        key={i}
                        step={step}
                        isExpanded={expandedStepIndex === i}
                        onToggle={() =>
                          setExpandedStepIndex(
                            expandedStepIndex === i ? null : i
                          )
                        }
                      />
                    ))}
                  </div>
                </>
              )}
              {log.process.error && (
                <ErrorBox
                  message={
                    formatWorkflowErrorMessage(t, log.process.error) ??
                    formatHistoryErrorMessage(t)
                  }
                  size="small"
                />
              )}
            </StageSection>
          )}

          {/* OUTPUT Stage */}
          {log.output && (
            <StageSection
              title={t['com.streamlain.streamlain.history.stage-output']()}
            >
              {outputModeLabel && (
                <DataRow
                  label={t['com.streamlain.streamlain.history.mode']()}
                  value={outputModeLabel}
                />
              )}
              {log.output.targetTable && (
                <DataRow
                  label={t['com.streamlain.streamlain.history.target-table']()}
                  value={log.output.targetTable}
                />
              )}
              {historyRunWritesNothing(log.output) ? (
                <div className={styles.muted}>
                  {t['com.streamlain.automation.panel.no-output']()}
                </div>
              ) : (
                <DataRow
                  label={t['com.streamlain.streamlain.history.rows-written']()}
                  value={log.output.rowsWritten}
                  tone="success"
                />
              )}
              {log.output.rowsFailed > 0 && (
                <DataRow
                  label={t['com.streamlain.streamlain.history.rows-failed']()}
                  value={log.output.rowsFailed}
                  tone="error"
                />
              )}
              {log.output.written && (
                <WrittenTablesRows tables={log.output.written} />
              )}
              {log.output.outputMappings &&
                log.output.outputMappings.length > 0 && (
                  <>
                    <div className={styles.subLabel}>
                      {t['com.streamlain.streamlain.history.output-mappings']()}
                    </div>
                    <JsonPreview data={log.output.outputMappings} />
                  </>
                )}
            </StageSection>
          )}

          {/* Global Error */}
          {log.terminalReason && (
            <ErrorBox
              message={t[TERMINAL_REASON_TEXT[log.terminalReason]]()}
              size="medium"
            />
          )}
          {log.error && (
            <ErrorBox
              message={
                formatWorkflowErrorMessage(t, log.error) ??
                formatHistoryErrorMessage(t)
              }
              size="medium"
            />
          )}
          {cancelError && <ErrorBox message={cancelError} size="small" />}

          {/* Delete only terminal logs. sl-67kc.3.3: also blocked while a
              long-running op is parked so its suspension row is not orphaned. */}
          {!isActive && !canCancelExecution && (
            <Button
              className={styles.deleteButton}
              prefix={<DeleteIcon />}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                if (deleteBlocked) return;
                onDelete();
              }}
              disabled={deleteBlocked}
              title={
                deleteBlocked
                  ? t['com.streamlain.streamlain.history.lro.delete-blocked']()
                  : undefined
              }
            >
              {t['com.streamlain.streamlain.history.delete-log']()}
            </Button>
          )}
        </div>
      </AnimatedRegion>
    </div>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export const HistoryPanel: FC<HistoryPanelProps> = ({ blockId }) => {
  const t = useI18n();
  const loggingService = useService(LoggingService);
  const workspaceService = useService(WorkspaceService);
  const automationService = useService(AutomationService);
  const workspaceId = workspaceService.workspace.id;
  const { openConfirmModal } = useConfirmModal();
  const pendingRowTarget = useLiveData(
    automationService.pendingHistoryRowTarget$
  );

  // Active log from LoggingService (in-memory, for running executions)
  const activeLog = useLiveData(loggingService.getActiveLog$(blockId));

  // Logs from LoggingService (backend logs loaded via loadFromBackend)
  const backendLogs = useLiveData(loggingService.getLogs$(blockId));
  // sl-67kc.3.4 REWORK round 3: track when the initial backend fetch
  // settled (success OR error) so the chip deep-link scroll effect can
  // wait for logs to populate before consuming `pendingHistoryRowTarget$`.
  //
  // Round 4 (Codex cross-block race): `automation-panel.tsx:1710` reuses
  // the same HistoryPanel instance across block switches, so a stale
  // `.finally()` from the PREVIOUS block's fetch could fire AFTER
  // `blockId` changed and flip the gate open for the new block before
  // its own fetch settled. The effect cleanup now sets a cancelled
  // flag the promise chain reads, so only the current block's fetch
  // can transition `backendLoaded` to `true`.
  const [backendLoaded, setBackendLoaded] = useState(false);
  useEffect(() => {
    setBackendLoaded(false);
    let cancelled = false;
    loggingService
      .loadFromBackend(blockId, workspaceId)
      .catch(console.error)
      .finally(() => {
        if (cancelled) return;
        setBackendLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [blockId, workspaceId, loggingService]);

  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
  const listRef = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback((logId: string) => {
    setExpandedLogId(current => (current === logId ? null : logId));
  }, []);

  // Delete a single log via backend API
  const handleDelete = useCallback(
    (logId: string): void => {
      // Update UI immediately
      loggingService.deleteLog(blockId, logId);
      if (expandedLogId === logId) {
        setExpandedLogId(null);
      }

      // Delete from backend asynchronously
      workspaceApiFetch(
        workflowWorkspaceRoutes.log(workspaceId, logId),
        {
          method: 'DELETE',
        },
        historyForegroundPolicy(workspaceId, `delete-log:${logId}`)
      )
        .then(response => {
          if (!response.ok) {
            console.error(
              '[HistoryPanel] Backend delete failed:',
              response.status
            );
            // Refresh to restore if backend failed
            return loggingService.refreshFromBackend(blockId, workspaceId);
          }
          return undefined;
        })
        .catch(err => {
          console.error('[HistoryPanel] Failed to delete log:', err);
          // Refresh to restore on error
          loggingService
            .refreshFromBackend(blockId, workspaceId)
            .catch(console.error);
        });
    },
    [blockId, expandedLogId, loggingService, workspaceId]
  );

  const refreshBlockLogs = useCallback(
    () => loggingService.refreshFromBackend(blockId, workspaceId),
    [blockId, loggingService, workspaceId]
  );
  const {
    cancelOp: handleCancelOp,
    cancelExecution: handleCancelExecution,
    retryOp: handleRetryOp,
    downloadOp: handleDownloadOp,
  } = useHistoryRunActions(workspaceId, refreshBlockLogs);

  const handleClearAll = useCallback((): void => {
    openConfirmModal({
      title: t['com.streamlain.streamlain.history.clear-confirm.title'](),
      description:
        t['com.streamlain.streamlain.history.clear-confirm.description'](),
      confirmText: t['com.streamlain.streamlain.history.clear-all'](),
      confirmButtonOptions: {
        variant: 'error',
      },
      onConfirm: () => {
        // Clear local state immediately for instant UI feedback
        loggingService.clearLogs(blockId);
        setExpandedLogId(null);

        // Then delete from backend asynchronously
        workspaceApiFetch(
          workflowWorkspaceRoutes.blockLogCollection(workspaceId, blockId),
          {
            method: 'DELETE',
          },
          historyForegroundPolicy(workspaceId, `clear-logs:${blockId}`)
        )
          .then(response => {
            if (!response.ok) {
              console.error(
                '[HistoryPanel] Backend delete failed:',
                response.status
              );
              // Refresh to restore if backend failed
              return loggingService.refreshFromBackend(blockId, workspaceId);
            }
            return undefined;
          })
          .catch(err => {
            console.error('[HistoryPanel] Failed to clear logs:', err);
            // Refresh to restore on error
            loggingService
              .refreshFromBackend(blockId, workspaceId)
              .catch(console.error);
          });
      },
    });
  }, [blockId, loggingService, openConfirmModal, t, workspaceId]);

  // Combine backend logs with active log (if running)
  const allLogs = useMemo(() => {
    const logsById = new Map<string, ExecutionLog>();

    for (const log of backendLogs) {
      logsById.set(log.id, log);
    }

    if (activeLog) {
      const existing = logsById.get(activeLog.id);
      logsById.set(
        activeLog.id,
        existing ? { ...existing, ...activeLog } : activeLog
      );
    }

    return Array.from(logsById.values()).sort(
      (a, b) => b.startedAt - a.startedAt
    );
  }, [backendLogs, activeLog]);

  // sl-67kc.3.4 REWORK round 3: the workspace operations chip deep-link
  // activates the target block on AutomationService and publishes the
  // target via `pendingHistoryRowTarget$`. This panel is block-scoped
  // per `automation-panel.tsx:1710`, so the effect only matters for the
  // HistoryPanel whose blockId matches the target.
  //
  // Timing gate (Codex round 3 F1): we must NOT consume the target until
  //   (a) `loadFromBackend()` settles (tracked by `backendLoaded`), AND
  //   (b) the target log is actually in `allLogs` so the `rowRef`
  //       callback has a LogEntry to attach to.
  // Otherwise first-open deep-links raced the network fetch — consume
  // cleared the target, `rowRefs.current.get(targetId)` was undefined,
  // and the scroll silently no-op'd.
  //
  // Once backendLoaded is true and the target is STILL missing from
  // `allLogs` (log was deleted, or the target was for a different
  // block that was already consumed upstream), we consume anyway to
  // drop the stale target — otherwise it would sit in LiveData forever.
  useEffect(() => {
    if (!pendingRowTarget || pendingRowTarget.blockId !== blockId) return;
    if (!backendLoaded) return;
    const targetId = pendingRowTarget.executionLogId;
    const targetExists = allLogs.some(l => l.id === targetId);
    automationService.consumePendingHistoryRowTarget(blockId);
    if (!targetExists) return;
    setExpandedLogId(targetId);
    // setExpandedLogId queues a render; the LogEntry's rowRef callback
    // fires during that commit. requestAnimationFrame runs after
    // commit, so the lookup sees the freshly-attached DOM node.
    requestAnimationFrame(() => {
      const list = listRef.current;
      const row = rowRefs.current.get(targetId);
      if (!list || !row) return;
      const offset =
        row.getBoundingClientRect().top -
        list.getBoundingClientRect().top -
        (list.clientHeight - row.offsetHeight) / 2;
      streamLainAnimationController.scrollTo(list, {
        top: Math.max(
          0,
          Math.min(
            list.scrollTop + offset,
            list.scrollHeight - list.clientHeight
          )
        ),
      });
    });
  }, [pendingRowTarget, blockId, automationService, allLogs, backendLoaded]);

  // Calculate stats from all logs
  const stats = useMemo(() => {
    if (allLogs.length === 0) {
      return {
        totalExecutions: 0,
        totalRowsProcessed: 0,
        successRate: 0,
      };
    }

    const completedLogs = allLogs.filter(
      (l: ExecutionLog) => l.status === 'completed'
    );
    const totalRowsProcessed = allLogs.reduce(
      (sum: number, l: ExecutionLog) => sum + (l.input?.passedFilter ?? 0),
      0
    );

    return {
      totalExecutions: allLogs.length,
      totalRowsProcessed,
      successRate:
        allLogs.length > 0
          ? Math.round((completedLogs.length / allLogs.length) * 100)
          : 0,
      lastExecution: allLogs[0]?.startedAt,
    };
  }, [allLogs]);

  const successTone: styles.HistoryTone =
    stats.successRate >= 80
      ? 'success'
      : stats.successRate >= 50
        ? 'warning'
        : 'error';

  return (
    <div className={clsx(panelStyles.section, styles.panel)}>
      {/* Stats Summary with Clear All Button */}
      {stats.totalExecutions > 0 && (
        <div className={styles.stats}>
          <div className={styles.statList}>
            <div className={styles.stat}>
              <span className={styles.statLabel}>
                {t['com.streamlain.streamlain.history.runs']()}
              </span>
              <span className={styles.statValue}>{stats.totalExecutions}</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statLabel}>
                {t['com.streamlain.streamlain.history.rows']()}
              </span>
              <span className={styles.statValue}>
                {stats.totalRowsProcessed}
              </span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statLabel}>
                {t['com.streamlain.streamlain.history.success']()}
              </span>
              <span
                className={clsx(
                  styles.statValue,
                  styles.statValueTone[successTone]
                )}
              >
                {stats.successRate}%
              </span>
            </div>
          </div>
          <button
            type="button"
            className={styles.iconButton}
            onClick={handleClearAll}
            title={t['com.streamlain.streamlain.history.clear-all']()}
            aria-label={t['com.streamlain.streamlain.history.clear-all']()}
          >
            <DeleteIcon />
          </button>
        </div>
      )}

      {/* Log List */}
      {allLogs.length === 0 ? (
        <div className={styles.empty}>
          {t['com.streamlain.streamlain.history.empty']()}
        </div>
      ) : (
        <div ref={listRef} className={styles.list}>
          {allLogs.map((log: ExecutionLog) => (
            <LogEntry
              key={log.id}
              log={log}
              isExpanded={expandedLogId === log.id}
              onToggle={() => handleToggle(log.id)}
              onDelete={() => handleDelete(log.id)}
              onCancelOp={handleCancelOp}
              onCancelExecution={handleCancelExecution}
              onRetryOp={handleRetryOp}
              onDownloadOp={handleDownloadOp}
              isActive={activeLog?.id === log.id}
              rowRef={el => {
                if (el) rowRefs.current.set(log.id, el);
                else rowRefs.current.delete(log.id);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};
